/**
 * P2-20: LeftPanel —— 左栏原子切换的组装根（对齐 WLeftPanel，js/jsx/left.jsx.js:835-950）。
 *
 * 区块顺序与 id/class 钩子逐一对齐旧实现：
 *   ResourceTable → #advisorsContainer → #fastHuntContainer →
 *   #fastSacrificeContainer → #fastPraiseContainer → Pins → CraftTable
 * （left.jsx.js:881-929）。旧版外层是 `$r("div", null, [...])` 无 class 包装；
 * 新版由 TooltipProvider 统一承载（不产生额外 DOM 包装，仅 context + 激活时
 * 的浮层 div），视觉结构与旧版一致。
 *
 * - advisors 断粮警告：旧版内联在 WLeftPanel.render（left.jsx.js:867-889），
 *   js/ui.js 的 updateAdvisors 已是空函数（js/ui.js:768-769），此处数据驱动：
 *   field.on > 0 且 catnip 存量 + 冬季剩余 tick 产量 <= 0 时 display:block，
 *   否则 display:none（div 恒渲染，对齐旧版行内 display 切换语义）；
 * - fastHunt/fastSacrifice/fastPraise：复用 FastActions.tsx 的三个组件
 *   （display/visibility 双层条件逐条对齐 INVENTORY）；fastPraise 已按
 *   religion.faith > 0 数据驱动，引擎侧 updateFastPraise 桥接同步删除；
 * - TooltipProvider 上提到本根（ResourceTable/CraftTable 文件头注明的既定
 *   演进）；各子面板文件内的独立挂载入口（带各自 Provider 的 default export）
 *   保留作隔离测试用，不再注册到 KGNewUI.panels。
 *
 * 字号缩放兼容（INVENTORY 风险点 4）：本岛挂载在 #leftColumn 外壳**内部**的
 * #leftColumnViewport 锚点上，引擎 updateFontSize 写外壳 font-size
 * （js/ui.js:891-893）经继承作用于全部新组件，无需桥接。
 */
import { useCallback } from "react";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { TooltipProvider } from "../../components/Tooltip/TooltipProvider";
import { ResourceTable } from "./ResourceTable";
import { Pins } from "./Pins";
import { CraftTable } from "./CraftTable";
import { FastHunt, FastSacrifice, FastPraise } from "./FastActions";

// ---------------------------------------------------------------------------
// Advisors 断粮警告（left.jsx.js:867-889 内联段的提取）
// ---------------------------------------------------------------------------

/**
 * 对齐旧版逐 tick 求值：field.on > 0 时，
 * catnip.value + winterDays * winterCatnipPerTick * ticksPerDay <= 0 → 显示。
 * winterDays = daysPerSeason -（当前为冬季 ? calendar.day : 0）。
 */
export function computeShowAdvisor(game: GamePage | null): boolean | null {
  if (!game) {
    return null;
  }
  if (game.bld.get("field").on <= 0) {
    return false;
  }
  const calendar = game.calendar;
  const winterDays =
    calendar.daysPerSeason - (calendar.getCurSeason().name === "winter" ? calendar.day : 0);
  const catnipPerTick = game.winterCatnipPerTick;
  return game.resPool.get("catnip").value + winterDays * catnipPerTick * calendar.ticksPerDay <= 0;
}

export function AdvisorsWarning() {
  const selector = useCallback((game: GamePage | null) => computeShowAdvisor(game), []);
  const show = useGameTick(selector);

  if (show === null) {
    return null;
  }
  return (
    <div
      id="advisorsContainer"
      style={{ paddingTop: "10px", display: show ? "block" : "none" }}
    >
      {t("general.food.advisor.text")}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeftPanel
// ---------------------------------------------------------------------------

/**
 * 面板入口。通过 KGNewUI.panels.Left 挂载到 #leftColumnViewport
 * （index.html initGame，P2-20 原子切换；旧 WLeftPanel 挂载 js/ui.js:596-598
 * 已同步注释停用）。
 */
export default function LeftPanel() {
  return (
    <TooltipProvider>
      <ResourceTable />
      <AdvisorsWarning />
      <FastHunt />
      <FastSacrifice />
      <FastPraise />
      <Pins />
      <CraftTable />
    </TooltipProvider>
  );
}
